import { clamp } from './cosmicMath';

export const targetFrameIntervalMs = 1000 / 60;

export interface ScenePerformanceProfile {
  galaxyPointCount: number;
  maxPixelRatio: number;
  frameIntervalMs: number;
  scrollFrameIntervalMs: number;
  torusTubularSegments: number;
}

export interface ScenePerformanceOptions {
  constrainedDevice?: boolean;
  intenseVisualFeatureCount?: number;
}

export interface AdaptiveRenderState {
  pixelRatio: number;
  sustainedSlowFrameCount: number;
}

export const defaultScenePerformanceProfile: ScenePerformanceProfile = {
  galaxyPointCount: 900,
  maxPixelRatio: 1.5,
  frameIntervalMs: targetFrameIntervalMs,
  scrollFrameIntervalMs: 1000 / 30,
  torusTubularSegments: 160,
};

export const constrainedScenePerformanceProfile: ScenePerformanceProfile = {
  galaxyPointCount: 520,
  maxPixelRatio: 1,
  frameIntervalMs: targetFrameIntervalMs,
  scrollFrameIntervalMs: 1000 / 20,
  torusTubularSegments: 88,
};

export const reducedMotionScenePerformanceProfile: ScenePerformanceProfile = {
  galaxyPointCount: 420,
  maxPixelRatio: 1,
  frameIntervalMs: 1000 / 20,
  scrollFrameIntervalMs: 1000 / 20,
  torusTubularSegments: 80,
};

const intenseVisualFeatureIds = new Set([
  'animated-title',
  'background-animation',
  'color-wheel',
  'global-lighting',
  'orbital-ring',
]);

export function getIntenseVisualFeatureCount(featureIds: readonly string[]): number {
  return featureIds.filter((featureId) => intenseVisualFeatureIds.has(featureId)).length;
}

export function shouldUseConstrainedPerformanceProfile({
  constrainedDevice = false,
  intenseVisualFeatureCount = 0,
}: ScenePerformanceOptions): boolean {
  return constrainedDevice && intenseVisualFeatureCount >= 4;
}

export function getScenePerformanceProfile(
  reduceMotion: boolean,
  options: ScenePerformanceOptions = {},
): ScenePerformanceProfile {
  if (reduceMotion) {
    return reducedMotionScenePerformanceProfile;
  }

  if (shouldUseConstrainedPerformanceProfile(options)) {
    return constrainedScenePerformanceProfile;
  }

  return defaultScenePerformanceProfile;
}

export function getOptimizedPixelRatio(devicePixelRatio: number, maxPixelRatio: number): number {
  return clamp(devicePixelRatio || 1, 1, maxPixelRatio);
}

export function shouldRenderFrame(
  timestampMs: number,
  lastFrameTimestampMs: number,
  frameIntervalMs: number,
  scrollFrameIntervalMs: number,
  isScrolling: boolean,
): boolean {
  const cappedFrameIntervalMs = Math.max(frameIntervalMs, targetFrameIntervalMs);
  const minimumFrameIntervalMs = isScrolling ? Math.max(cappedFrameIntervalMs, scrollFrameIntervalMs) : cappedFrameIntervalMs;
  return timestampMs - lastFrameTimestampMs >= minimumFrameIntervalMs;
}

export function createAdaptiveRenderState(pixelRatio: number): AdaptiveRenderState {
  return {
    pixelRatio,
    sustainedSlowFrameCount: 0,
  };
}

export function getNextAdaptiveRenderState(
  state: AdaptiveRenderState,
  frameDeltaMs: number,
  minimumPixelRatio = 1,
): AdaptiveRenderState {
  const slowFrameThresholdMs = targetFrameIntervalMs * 2;
  const sustainedSlowFrameCount = frameDeltaMs > slowFrameThresholdMs
    ? state.sustainedSlowFrameCount + 1
    : 0;

  if (sustainedSlowFrameCount < 3 || state.pixelRatio <= minimumPixelRatio) {
    return {
      pixelRatio: state.pixelRatio,
      sustainedSlowFrameCount,
    };
  }

  return {
    pixelRatio: Math.max(minimumPixelRatio, Number((state.pixelRatio - 0.25).toFixed(2))),
    sustainedSlowFrameCount: 0,
  };
}
